//mar11 managedFields.js without column save logic:
// src/utils/managedFields.js
// -----------------------------------------------------------------------------
// Centralized service layer for organization-managed fields and managed option sets.
//
// PURPOSE
//  • Provide the source-of-truth CRUD + validation helpers for org-managed fields
//  • Keep governance logic OUT of form runtime components
//  • Preserve stable option identity (optionId) across edits / lifecycle changes
//  • Support enterprise-safe lifecycle rules: active / inactive / pending / archived
//  • Prepare the app for request/approval workflows without forcing them yet
//
// IMPORTANT PRINCIPLES
//  • Form builder fields remain form-local instances (new fieldId + new ufi)
//  • Org-managed definitions remain org-global and stable
//  • Managed option sets are NEVER hard-deleted here once in use; archive/deactivate instead
//  • This file is intentionally additive and isolated so it does not break the
//    current form builder or DAM flows while we introduce org-wide fields safely
//
// NOTES
//  • This file does NOT enforce UI permissions directly. Callers should gate actions
//    through AuthContext / permissions checks before calling mutating functions.
//  • We still validate aggressively here for backend/domain safety.
//  • We use explicit IDs (managedFieldId / optionsSetId) so references remain stable.
// -----------------------------------------------------------------------------

import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
} from 'firebase/firestore';

const { v4: uuidv4 } = require('uuid');

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

export const MANAGED_FIELD_TYPES = Object.freeze([
  'single-select',
  'multi-select',
]);

export const MANAGED_FIELD_STATUS = Object.freeze({
  ACTIVE: 'active',
  ARCHIVED: 'archived',
});

export const MANAGED_OPTION_LIFECYCLE = Object.freeze({
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  ARCHIVED: 'archived',
});

export const MANAGED_REQUEST_STATUS = Object.freeze({
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  APPLIED: 'applied',
});

export const MANAGED_REQUEST_TYPES = Object.freeze({
  ADD_OPTION: 'add-option',
  EDIT_OPTION: 'edit-option',
  EXPIRE_OPTION: 'expire-option',
  REACTIVATE_OPTION: 'reactivate-option',
});

// -----------------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------------

const assertOrganizationId = (organizationId) => {
  if (!organizationId || typeof organizationId !== 'string') {
    throw new Error('[managedFields] organizationId is required.');
  }
};

const assertUserId = (userId) => {
  if (!userId || typeof userId !== 'string') {
    throw new Error('[managedFields] userId is required.');
  }
};

const normalizeString = (value, fallback = '') => {
  return typeof value === 'string' ? value.trim() : fallback;
};

const normalizeNullableString = (value) => {
  const s = normalizeString(value, '');
  return s.length > 0 ? s : null;
};

const isValidManagedFieldType = (type) => {
  return MANAGED_FIELD_TYPES.includes(type);
};

const isValidManagedFieldStatus = (status) => {
  return Object.values(MANAGED_FIELD_STATUS).includes(status);
};

const isValidOptionLifecycleStatus = (status) => {
  return Object.values(MANAGED_OPTION_LIFECYCLE).includes(status);
};

const normalizeGovernance = (governance = {}) => ({
  allowLocalLabelOverride:
    governance?.allowLocalLabelOverride === true,
  allowLocalRequiredOverride:
    governance?.allowLocalRequiredOverride === true,
  allowLocalHelperTextOverride:
    governance?.allowLocalHelperTextOverride === true,
  allowLocalPlaceholderOverride:
    governance?.allowLocalPlaceholderOverride === true,
  allowLocalOptionEditing:
    governance?.allowLocalOptionEditing === true,
  allowLocalDependencies:
    governance?.allowLocalDependencies === true,
  allowOther:
    governance?.allowOther === true,
});

const normalizeDisplayDefaults = (displayDefaults = {}) => ({
  placeholder: normalizeString(displayDefaults?.placeholder, ''),
  helperText: normalizeString(displayDefaults?.helperText, ''),
});

const normalizeManagedFieldDoc = ({
  id = null,
  label,
  type,
  semanticKey = null,
  description = '',
  optionsSource = {},
  governance = {},
  displayDefaults = {},
  status = MANAGED_FIELD_STATUS.ACTIVE,
  version = 1,
}) => {
  const normalizedType = normalizeString(type);
  const normalizedStatus = normalizeString(status, MANAGED_FIELD_STATUS.ACTIVE);

  if (!isValidManagedFieldType(normalizedType)) {
    throw new Error(
      `[managedFields] Invalid managed field type: ${normalizedType}`
    );
  }

  if (!isValidManagedFieldStatus(normalizedStatus)) {
    throw new Error(
      `[managedFields] Invalid managed field status: ${normalizedStatus}`
    );
  }

  const managedFieldId = normalizeString(id, uuidv4());
  const normalizedLabel = normalizeString(label);

  if (!normalizedLabel) {
    throw new Error('[managedFields] Managed field label is required.');
  }

  const normalizedOptionsSource = {
    type: normalizeString(optionsSource?.type, 'managedOptionSet'),
    optionsSetId: normalizeString(optionsSource?.optionsSetId),
  };

  if (
    (normalizedType === 'single-select' || normalizedType === 'multi-select') &&
    !normalizedOptionsSource.optionsSetId
  ) {
    throw new Error(
      '[managedFields] Dropdown managed fields require optionsSource.optionsSetId.'
    );
  }

  return {
    id: managedFieldId,
    label: normalizedLabel,
    type: normalizedType,
    scope: 'organization',
    status: normalizedStatus,
    semanticKey: normalizeNullableString(semanticKey),
    description: normalizeString(description, ''),
    optionsSource: normalizedOptionsSource,
    governance: normalizeGovernance(governance),
    displayDefaults: normalizeDisplayDefaults(displayDefaults),
    lifecycle: {
      archived: normalizedStatus === MANAGED_FIELD_STATUS.ARCHIVED,
    },
    version: Number.isFinite(Number(version)) ? Number(version) : 1,
  };
};

const normalizeManagedOption = (option = {}, index = 0) => {
  const optionId = normalizeString(option?.optionId, uuidv4());
  const label = normalizeString(option?.label);
  const value = normalizeString(option?.value, label);
  const hint = normalizeString(option?.hint, '');
  const order = Number.isFinite(Number(option?.order))
    ? Number(option.order)
    : index + 1;

  const lifecycleStatus = normalizeString(
    option?.lifecycleStatus,
    MANAGED_OPTION_LIFECYCLE.ACTIVE
  );

  if (!label) {
    throw new Error('[managedFields] Managed option label is required.');
  }

  if (!isValidOptionLifecycleStatus(lifecycleStatus)) {
    throw new Error(
      `[managedFields] Invalid option lifecycle status: ${lifecycleStatus}`
    );
  }

  return {
    optionId,
    label,
    value,
    hint,
    order,
    lifecycleStatus,
    liveFrom: option?.liveFrom || null,
    liveTo: option?.liveTo || null,
    createdAt: option?.createdAt || null,
    createdBy: option?.createdBy || null,
    updatedAt: option?.updatedAt || null,
    updatedBy: option?.updatedBy || null,
  };
};

const normalizeManagedOptionSetDoc = ({
  id = null,
  label,
  status = MANAGED_FIELD_STATUS.ACTIVE,
  options = [],
}) => {
  const optionsSetId = normalizeString(id, uuidv4());
  const normalizedLabel = normalizeString(label);
  const normalizedStatus = normalizeString(status, MANAGED_FIELD_STATUS.ACTIVE);

  if (!normalizedLabel) {
    throw new Error('[managedFields] Managed option set label is required.');
  }

  if (!isValidManagedFieldStatus(normalizedStatus)) {
    throw new Error(
      `[managedFields] Invalid managed option set status: ${normalizedStatus}`
    );
  }

  const normalizedOptions = Array.isArray(options)
    ? options.map((opt, index) => normalizeManagedOption(opt, index))
    : [];

  // Stable identity check: no duplicate optionIds
  const ids = normalizedOptions.map((o) => o.optionId);
  const uniqueIds = new Set(ids);
  if (uniqueIds.size !== ids.length) {
    throw new Error(
      '[managedFields] Managed option set contains duplicate optionIds.'
    );
  }

  return {
    id: optionsSetId,
    label: normalizedLabel,
    status: normalizedStatus,
    options: normalizedOptions,
  };
};

const toDateSafe = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // IMPORTANT:
    // Treat yyyy-mm-dd as a LOCAL calendar date, not a UTC timestamp.
    // This prevents end dates like "today" from being shifted backward by timezone
    // when runtime visibility checks run in the browser.
    const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, yyyyRaw, mmRaw, ddRaw] = dateOnlyMatch;
      const yyyy = Number(yyyyRaw);
      const mm = Number(mmRaw);
      const dd = Number(ddRaw);

      const localDate = new Date(yyyy, mm - 1, dd);

      if (Number.isNaN(localDate.getTime())) return null;
      return localDate;
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const toStartOfDay = (value) => {
  const date = toDateSafe(value);
  if (!date) return null;

  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const toEndOfDay = (value) => {
  const date = toDateSafe(value);
  if (!date) return null;

  const normalized = new Date(date);
  normalized.setHours(23, 59, 59, 999);
  return normalized;
};

// -----------------------------------------------------------------------------
// Lifecycle resolver
// -----------------------------------------------------------------------------

/**
 * Returns true when a managed option should be selectable "right now".
 * Rules:
 *  • archived -> never selectable
 *  • pending  -> never selectable
 *  • inactive -> never selectable
 *  • active   -> selectable if within live window (if provided)
 *
 * This helper is intentionally conservative and deterministic so the caller
 * can use the same rule in the builder preview, final runtime, and validation.
 *
 * IMPORTANT
 *  • active means the option is approved/live in principle
 *  • if liveFrom/liveTo are present, the current date must fall inside that window
 *  • if no live window exists, active options are selectable indefinitely
 */
export const isManagedOptionCurrentlyLive = (
  option,
  now = new Date()
) => {
  if (!option || typeof option !== 'object') return false;

  const lifecycleStatus = normalizeString(
    option?.lifecycleStatus,
    MANAGED_OPTION_LIFECYCLE.ACTIVE
  );

  if (
    lifecycleStatus === MANAGED_OPTION_LIFECYCLE.ARCHIVED ||
    lifecycleStatus === MANAGED_OPTION_LIFECYCLE.PENDING ||
    lifecycleStatus === MANAGED_OPTION_LIFECYCLE.INACTIVE
  ) {
    return false;
  }

  // IMPORTANT:
  // - liveFrom is inclusive from the START of that date
  // - liveTo is inclusive through the END of that date
  // This ensures an option with end date "today" remains visible for the
  // full day instead of disappearing at 12:00 AM.
  const liveFrom = toStartOfDay(option?.liveFrom);
  const liveTo = toEndOfDay(option?.liveTo);

  if (liveFrom && now < liveFrom) {
    return false;
  }

  if (liveTo && now > liveTo) {
    return false;
  }

  return true;
};

/**
 * Returns the active/live options for end-user rendering.
 * This function intentionally strips governance-only lifecycle concerns
 * down to the shape expected by your existing select field pipeline.
 */
export const resolveLiveManagedOptionsForRuntime = (
  managedOptionSetDoc,
  now = new Date()
) => {
  const sourceOptions = Array.isArray(managedOptionSetDoc?.options)
    ? managedOptionSetDoc.options
    : [];

  return sourceOptions
    .filter((opt) => isManagedOptionCurrentlyLive(opt, now))
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((opt) => ({
      optionId: opt.optionId,
      label: opt.label,
      value: opt.value,
      hint: opt.hint || '',
      order: opt.order || 0,
    }));
};

// -----------------------------------------------------------------------------
// Managed fields CRUD
// -----------------------------------------------------------------------------

export const createManagedField = async ({
  organizationId,
  userId,
  managedField,
}) => {
  assertOrganizationId(organizationId);
  assertUserId(userId);

  const normalized = normalizeManagedFieldDoc(managedField);

  const ref = doc(
    db,
    'organizations',
    organizationId,
    'managedFields',
    normalized.id
  );

  const existing = await getDoc(ref);
  if (existing.exists()) {
    throw new Error(
      `[managedFields] Managed field "${normalized.id}" already exists.`
    );
  }

  const payload = {
    ...normalized,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };

  await setDoc(ref, payload);

  console.log('[managedFields] Managed field created successfully:', {
    organizationId,
    managedFieldId: normalized.id,
    type: normalized.type,
    label: normalized.label,
  });

  return {
    status: 'success',
    managedFieldId: normalized.id,
    data: payload,
  };
};

export const updateManagedField = async ({
  organizationId,
  userId,
  managedFieldId,
  updates,
}) => {
  assertOrganizationId(organizationId);
  assertUserId(userId);

  const normalizedId = normalizeString(managedFieldId);
  if (!normalizedId) {
    throw new Error('[managedFields] managedFieldId is required.');
  }

  const ref = doc(
    db,
    'organizations',
    organizationId,
    'managedFields',
    normalizedId
  );

  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error(
      `[managedFields] Managed field "${normalizedId}" does not exist.`
    );
  }

  const current = snap.data() || {};
  const next = normalizeManagedFieldDoc({
    ...current,
    ...updates,
    id: normalizedId,
    version: (Number(current?.version || 1) + 1),
  });

  const payload = {
    ...next,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };

  await updateDoc(ref, payload);

  console.log('[managedFields] Managed field updated successfully:', {
    organizationId,
    managedFieldId: normalizedId,
    version: next.version,
  });

  return {
    status: 'success',
    managedFieldId: normalizedId,
    data: payload,
  };
};

export const archiveManagedField = async ({
  organizationId,
  userId,
  managedFieldId,
}) => {
  return updateManagedField({
    organizationId,
    userId,
    managedFieldId,
    updates: {
      status: MANAGED_FIELD_STATUS.ARCHIVED,
    },
  });
};

export const getManagedFieldById = async ({
  organizationId,
  managedFieldId,
}) => {
  assertOrganizationId(organizationId);

  const normalizedId = normalizeString(managedFieldId);
  if (!normalizedId) {
    throw new Error('[managedFields] managedFieldId is required.');
  }

  const ref = doc(
    db,
    'organizations',
    organizationId,
    'managedFields',
    normalizedId
  );

  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return { status: 'not_found', data: null };
  }

  return {
    status: 'success',
    data: {
      id: snap.id,
      ...snap.data(),
    },
  };
};

export const listManagedFieldsForOrganization = async ({
  organizationId,
  includeArchived = false,
  pageSize = 100,
}) => {
  assertOrganizationId(organizationId);

  const baseRef = collection(
    db,
    'organizations',
    organizationId,
    'managedFields'
  );

  const q = includeArchived
    ? query(baseRef, orderBy('label', 'asc'), limit(pageSize))
    : query(
        baseRef,
        where('status', '==', MANAGED_FIELD_STATUS.ACTIVE),
        orderBy('label', 'asc'),
        limit(pageSize)
      );

  const snap = await getDocs(q);

  const items = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));

  const sortedItems = [...items].sort((a, b) => {
    const aHasOrder = Number.isFinite(Number(a?.order));
    const bHasOrder = Number.isFinite(Number(b?.order));

    if (aHasOrder && bHasOrder) {
      return Number(a.order) - Number(b.order);
    }

    if (aHasOrder && !bHasOrder) {
      return -1;
    }

    if (!aHasOrder && bHasOrder) {
      return 1;
    }

    return normalizeString(a?.label, '').localeCompare(
      normalizeString(b?.label, '')
    );
  });

  console.log('[managedFields] Listed managed fields for organization:', {
    organizationId,
    count: sortedItems.length,
    includeArchived,
  });

  return {
    status: 'success',
    data: sortedItems,
  };
};

// -----------------------------------------------------------------------------
// Managed option sets CRUD
// -----------------------------------------------------------------------------

export const createManagedOptionSet = async ({
  organizationId,
  userId,
  optionSet,
}) => {
  assertOrganizationId(organizationId);
  assertUserId(userId);

  const normalized = normalizeManagedOptionSetDoc(optionSet);

  const ref = doc(
    db,
    'organizations',
    organizationId,
    'managedOptionSets',
    normalized.id
  );

  const existing = await getDoc(ref);
  if (existing.exists()) {
    throw new Error(
      `[managedFields] Managed option set "${normalized.id}" already exists.`
    );
  }

  const payload = {
    ...normalized,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };

  await setDoc(ref, payload);

  console.log('[managedFields] Managed option set created successfully:', {
    organizationId,
    optionsSetId: normalized.id,
    optionCount: normalized.options.length,
  });

  return {
    status: 'success',
    optionsSetId: normalized.id,
    data: payload,
  };
};

export const updateManagedOptionSet = async ({
  organizationId,
  userId,
  optionsSetId,
  updates,
}) => {
  assertOrganizationId(organizationId);
  assertUserId(userId);

  const normalizedId = normalizeString(optionsSetId);
  if (!normalizedId) {
    throw new Error('[managedFields] optionsSetId is required.');
  }

  const ref = doc(
    db,
    'organizations',
    organizationId,
    'managedOptionSets',
    normalizedId
  );

  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error(
      `[managedFields] Managed option set "${normalizedId}" does not exist.`
    );
  }

  const current = snap.data() || {};
  const next = normalizeManagedOptionSetDoc({
    ...current,
    ...updates,
    id: normalizedId,
  });

  const payload = {
    ...next,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };

  await updateDoc(ref, payload);

  console.log('[managedFields] Managed option set updated successfully:', {
    organizationId,
    optionsSetId: normalizedId,
    optionCount: next.options.length,
  });

  return {
    status: 'success',
    optionsSetId: normalizedId,
    data: payload,
  };
};

export const archiveManagedOptionSet = async ({
  organizationId,
  userId,
  optionsSetId,
}) => {
  return updateManagedOptionSet({
    organizationId,
    userId,
    optionsSetId,
    updates: {
      status: MANAGED_FIELD_STATUS.ARCHIVED,
    },
  });
};

export const getManagedOptionSetById = async ({
  organizationId,
  optionsSetId,
}) => {
  assertOrganizationId(organizationId);

  const normalizedId = normalizeString(optionsSetId);
  if (!normalizedId) {
    throw new Error('[managedFields] optionsSetId is required.');
  }

  const ref = doc(
    db,
    'organizations',
    organizationId,
    'managedOptionSets',
    normalizedId
  );

  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return { status: 'not_found', data: null };
  }

  return {
    status: 'success',
    data: {
      id: snap.id,
      ...snap.data(),
    },
  };
};

export const listManagedOptionSetsForOrganization = async ({
  organizationId,
  includeArchived = false,
  pageSize = 100,
}) => {
  assertOrganizationId(organizationId);

  const baseRef = collection(
    db,
    'organizations',
    organizationId,
    'managedOptionSets'
  );

  const q = includeArchived
    ? query(baseRef, orderBy('label', 'asc'), limit(pageSize))
    : query(
        baseRef,
        where('status', '==', MANAGED_FIELD_STATUS.ACTIVE),
        orderBy('label', 'asc'),
        limit(pageSize)
      );

  const snap = await getDocs(q);

  const items = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));

  console.log('[managedFields] Listed managed option sets for organization:', {
    organizationId,
    count: items.length,
    includeArchived,
  });

  return {
    status: 'success',
    data: items,
  };
};

// -----------------------------------------------------------------------------
// Request creation (phase-1 backend foundation)
// -----------------------------------------------------------------------------

export const createManagedFieldRequest = async ({
  organizationId,
  userId,
  requestType,
  managedFieldId,
  optionsSetId,
  payload,
}) => {
  assertOrganizationId(organizationId);
  assertUserId(userId);

  const normalizedRequestType = normalizeString(requestType);
  if (!Object.values(MANAGED_REQUEST_TYPES).includes(normalizedRequestType)) {
    throw new Error(
      `[managedFields] Invalid managed field request type: ${normalizedRequestType}`
    );
  }

  const requestId = uuidv4();
  const ref = doc(
    db,
    'organizations',
    organizationId,
    'managedFieldRequests',
    requestId
  );

  const docPayload = {
    id: requestId,
    managedFieldId: normalizeNullableString(managedFieldId),
    optionsSetId: normalizeNullableString(optionsSetId),
    requestType: normalizedRequestType,
    status: MANAGED_REQUEST_STATUS.PENDING,
    submittedBy: userId,
    submittedAt: serverTimestamp(),
    reviewedBy: null,
    reviewedAt: null,
    notes: '',
    payload: payload && typeof payload === 'object' ? payload : {},
  };

  await setDoc(ref, docPayload);

  console.log('[managedFields] Managed field request created successfully:', {
    organizationId,
    requestId,
    requestType: normalizedRequestType,
    managedFieldId,
    optionsSetId,
  });

  return {
    status: 'success',
    requestId,
    data: docPayload,
  };
};

// -----------------------------------------------------------------------------
// SmartLists canonical low-level write helpers
// -----------------------------------------------------------------------------
//
// PURPOSE
//  • Provide canonical write primitives for SmartLists Save
//  • Keep SmartLists spreadsheet orchestration OUT of this file
//  • Reuse the managed field / managed option set domain layer safely
//  • Preserve stable option identity (optionId) across SmartLists edits
//
// IMPORTANT
//  • These helpers are LOW-LEVEL canonical write primitives only
//  • They do NOT know about page state, canvas state, or spreadsheet dirty state
//  • They do NOT build SmartLists save plans
//  • They do NOT debounce anything
//  • They do NOT implement Save button orchestration
//
// EXPECTED USAGE
//  • SmartLists-specific service layer will later:
//      - build a canonical save plan from workspace state
//      - call these helpers in the correct order
//
// SAVE ORDER NOTE
//  • managed option sets should be written before managed fields when a field
//    references optionsSource.optionsSetId
//
// UI ↔ CANONICAL LIFECYCLE NOTE
//  • SmartLists and the canonical managed option lifecycle schema now share
//    the same vocabulary.
//
//    SmartLists / Canonical values:
//      - ''         (blank / unspecified, normalized to active)
//      - active
//      - inactive
//      - pending
//      - archived
//
//  • This helper section remains the correct boundary for normalization before
//    persistence, but it no longer translates to a different backend vocabulary.
//
// BUSINESS RULES
//  • Status should never persist as blank
//  • Blank/unset status means "active"
//  • A new option with no launch date and no end date is active indefinitely
//  • pending  -> pending
//  • inactive -> inactive
//  • archived -> archived
//  • active   -> active
// -----------------------------------------------------------------------------

/**
 * Normalize SmartLists lifecycle values into the canonical managed option
 * lifecycle schema.
 *
 * SMARTLISTS / CANONICAL VALUES
 *  • ''         -> active
 *  • active     -> active
 *  • inactive   -> inactive
 *  • pending    -> pending
 *  • archived   -> archived
 *
 * IMPORTANT
 *  • Blank/unset should never survive into canonical persistence
 *  • We intentionally default blank to ACTIVE so:
 *      - the Status column never persists as blank
 *      - new options without dates become active indefinitely
 *  • We also accept canonical values directly, defensively, so this helper
 *    remains stable if upstream callers later pass canonical terms.
 */
const normalizeSmartListLifecycleStatusForCanonicalWrite = (value) => {
  const normalized = normalizeString(value, '').toLowerCase();

  // Blank/unset in the SmartLists UI means “active indefinitely”
  if (!normalized) {
    return MANAGED_OPTION_LIFECYCLE.ACTIVE;
  }

  // SmartLists / canonical vocabulary
  if (normalized === 'active') {
    return MANAGED_OPTION_LIFECYCLE.ACTIVE;
  }

  if (normalized === 'inactive') {
    return MANAGED_OPTION_LIFECYCLE.INACTIVE;
  }

  if (normalized === 'pending') {
    return MANAGED_OPTION_LIFECYCLE.PENDING;
  }

  if (normalized === 'archived') {
    return MANAGED_OPTION_LIFECYCLE.ARCHIVED;
  }

  // Defensive support for canonical values directly
  if (normalized === MANAGED_OPTION_LIFECYCLE.ACTIVE) {
    return MANAGED_OPTION_LIFECYCLE.ACTIVE;
  }

  if (normalized === MANAGED_OPTION_LIFECYCLE.INACTIVE) {
    return MANAGED_OPTION_LIFECYCLE.INACTIVE;
  }

  if (normalized === MANAGED_OPTION_LIFECYCLE.PENDING) {
    return MANAGED_OPTION_LIFECYCLE.PENDING;
  }

  if (normalized === MANAGED_OPTION_LIFECYCLE.ARCHIVED) {
    return MANAGED_OPTION_LIFECYCLE.ARCHIVED;
  }

  throw new Error(
    `[managedFields] Invalid SmartLists option lifecycle status: ${value}`
  );
};

/**
 * Normalize one SmartLists option row into the canonical managed option shape.
 *
 * IMPORTANT
 *  • Status never persists as blank
 *  • Blank/unset status defaults to ACTIVE
 *  • liveFrom/liveTo are preserved as provided
 *  • optionId remains stable if already present
 */
const normalizeSmartListOptionForCanonicalWrite = (option = {}, index = 0) => {
  const optionId = normalizeString(option?.optionId, uuidv4());
  const label = normalizeString(option?.label);
  const value = normalizeString(option?.value, label);
  const hint = normalizeString(option?.hint, '');
  const order = Number.isFinite(Number(option?.order))
    ? Number(option.order)
    : index + 1;

  const lifecycleStatus = normalizeSmartListLifecycleStatusForCanonicalWrite(
    option?.lifecycleStatus
  );

  if (!label) {
    throw new Error('[managedFields] SmartLists option label is required.');
  }

  return {
    optionId,
    label,
    value,
    hint,
    order,
    lifecycleStatus,
    liveFrom: option?.liveFrom || null,
    liveTo: option?.liveTo || null,
  };
};

const normalizeSmartListOptionSetPatch = (patch = {}) => {
  const label = normalizeString(patch?.label, 'Managed option set');
  const options = Array.isArray(patch?.options)
    ? patch.options.map((option, index) =>
        normalizeSmartListOptionForCanonicalWrite(option, index)
      )
    : [];

  const ids = options.map((opt) => opt.optionId);
  const uniqueIds = new Set(ids);

  if (uniqueIds.size !== ids.length) {
    throw new Error(
      '[managedFields] SmartLists option-set patch contains duplicate optionIds.'
    );
  }

  return {
    label,
    options: options.map((opt, index) => ({
      ...opt,
      order: index + 1,
    })),
  };
};

const normalizeSmartListManagedFieldPatch = (patch = {}) => {
  const normalizedType = normalizeString(patch?.type, 'single-select');
  const normalizedStatus = normalizeString(
    patch?.status,
    MANAGED_FIELD_STATUS.ACTIVE
  );

  if (!isValidManagedFieldType(normalizedType)) {
    throw new Error(
      `[managedFields] Invalid SmartLists managed field type: ${normalizedType}`
    );
  }

  if (!isValidManagedFieldStatus(normalizedStatus)) {
    throw new Error(
      `[managedFields] Invalid SmartLists managed field status: ${normalizedStatus}`
    );
  }

  const normalizedOptionsSource = {
    type: normalizeString(patch?.optionsSource?.type, 'managedOptionSet'),
    optionsSetId: normalizeString(patch?.optionsSource?.optionsSetId),
  };

  if (
    (normalizedType === 'single-select' || normalizedType === 'multi-select') &&
    !normalizedOptionsSource.optionsSetId
  ) {
    throw new Error(
      '[managedFields] SmartLists managed field patch requires optionsSource.optionsSetId.'
    );
  }

  const normalizedOrder = Number.isFinite(Number(patch?.order))
    ? Number(patch.order)
    : null;

  return {
    label: normalizeString(patch?.label, 'Managed field'),
    description: normalizeString(patch?.description, ''),
    type: normalizedType,
    status: normalizedStatus,
    semanticKey: normalizeNullableString(patch?.semanticKey),
    optionsSource: normalizedOptionsSource,
    governance: normalizeGovernance(patch?.governance || {}),
    displayDefaults: normalizeDisplayDefaults(patch?.displayDefaults || {}),

    // IMPORTANT:
    // SmartLists group order is persisted on the canonical managed field doc.
    // This lets the SmartLists sidepanel order and canvas column order survive
    // explicit Save and later reloads without affecting option-set behavior.
    ...(normalizedOrder !== null ? { order: normalizedOrder } : {}),
  };
};

/**
 * Apply one canonical SmartLists managed option set write.
 *
 * BEHAVIOR
 *  • Creates the managed option set if it does not exist
 *  • Otherwise updates the existing managed option set
 *  • Preserves stable optionId values already provided by the caller
 *  • Keeps lifecycle fields aligned with your canonical schema
 */
export const applySmartListOptionSetWrite = async ({
  organizationId,
  userId,
  optionsSetId,
  patch,
}) => {
  assertOrganizationId(organizationId);
  assertUserId(userId);

  const normalizedOptionsSetId = normalizeString(optionsSetId);
  if (!normalizedOptionsSetId) {
    throw new Error('[managedFields] optionsSetId is required for SmartLists save.');
  }

  const normalizedPatch = normalizeSmartListOptionSetPatch(patch);

  const ref = doc(
    db,
    'organizations',
    organizationId,
    'managedOptionSets',
    normalizedOptionsSetId
  );

  const snap = await getDoc(ref);
  const exists = snap.exists();

  if (!exists) {
    const payload = {
      id: normalizedOptionsSetId,
      label: normalizedPatch.label,
      status: MANAGED_FIELD_STATUS.ACTIVE,
      options: normalizedPatch.options,
      createdAt: serverTimestamp(),
      createdBy: userId,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    };

    await setDoc(ref, payload);

    console.log('[managedFields] SmartLists created managed option set successfully:', {
      organizationId,
      optionsSetId: normalizedOptionsSetId,
      optionCount: normalizedPatch.options.length,
    });

    return {
      status: 'success',
      message: 'Managed option set created successfully.',
      optionsSetId: normalizedOptionsSetId,
      data: payload,
    };
  }

  const current = snap.data() || {};

  const payload = {
    label: normalizedPatch.label,
    options: normalizedPatch.options,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
    status: current?.status || MANAGED_FIELD_STATUS.ACTIVE,
  };

  await updateDoc(ref, payload);

  console.log('[managedFields] SmartLists updated managed option set successfully:', {
    organizationId,
    optionsSetId: normalizedOptionsSetId,
    optionCount: normalizedPatch.options.length,
  });

  return {
    status: 'success',
    message: 'Managed option set updated successfully.',
    optionsSetId: normalizedOptionsSetId,
    data: {
      id: normalizedOptionsSetId,
      ...payload,
    },
  };
};

/**
 * Apply one canonical SmartLists managed field write.
 *
 * BEHAVIOR
 *  • Creates the managed field if it does not exist
 *  • Otherwise updates the existing managed field
 *  • Keeps optionsSource aligned to the managed option set
 *  • Uses the same normalization and versioning style as updateManagedField(...)
 */
export const applySmartListManagedFieldWrite = async ({
  organizationId,
  userId,
  managedFieldId,
  patch,
}) => {
  assertOrganizationId(organizationId);
  assertUserId(userId);

  const normalizedManagedFieldId = normalizeString(managedFieldId);
  if (!normalizedManagedFieldId) {
    throw new Error('[managedFields] managedFieldId is required for SmartLists save.');
  }

  const normalizedPatch = normalizeSmartListManagedFieldPatch(patch);

  const ref = doc(
    db,
    'organizations',
    organizationId,
    'managedFields',
    normalizedManagedFieldId
  );

  const snap = await getDoc(ref);
  const exists = snap.exists();

  if (!exists) {
    const payload = {
      id: normalizedManagedFieldId,
      label: normalizedPatch.label,
      type: normalizedPatch.type,
      scope: 'organization',
      status: normalizedPatch.status,
      semanticKey: normalizedPatch.semanticKey,
      description: normalizedPatch.description,
      optionsSource: normalizedPatch.optionsSource,
      governance: normalizedPatch.governance,
      displayDefaults: normalizedPatch.displayDefaults,

      // IMPORTANT:
      // Persist SmartLists group ordering on create so newly created managed
      // fields render in the same order as the SmartLists sidepanel/canvas.
      ...(Number.isFinite(Number(normalizedPatch?.order))
        ? { order: Number(normalizedPatch.order) }
        : {}),

      lifecycle: {
        archived: normalizedPatch.status === MANAGED_FIELD_STATUS.ARCHIVED,
      },
      version: 1,
      createdAt: serverTimestamp(),
      createdBy: userId,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    };

    await setDoc(ref, payload);

    console.log('[managedFields] SmartLists created managed field successfully:', {
      organizationId,
      managedFieldId: normalizedManagedFieldId,
      type: normalizedPatch.type,
      optionsSetId: normalizedPatch.optionsSource.optionsSetId,
    });

    return {
      status: 'success',
      message: 'Managed field created successfully.',
      managedFieldId: normalizedManagedFieldId,
      data: payload,
    };
  }

  const current = snap.data() || {};
  const nextVersion = Number.isFinite(Number(current?.version || 1))
    ? Number(current.version || 1) + 1
    : 2;

  const payload = {
    label: normalizedPatch.label,
    type: normalizedPatch.type,
    scope: current?.scope || 'organization',
    status: normalizedPatch.status,
    semanticKey: normalizedPatch.semanticKey,
    description: normalizedPatch.description,
    optionsSource: normalizedPatch.optionsSource,
    governance: normalizedPatch.governance,
    displayDefaults: normalizedPatch.displayDefaults,

    // IMPORTANT:
    // Persist SmartLists group ordering on update so reordered managed fields
    // remain stable across Save, reload, and later runtime resolution.
    ...(Number.isFinite(Number(normalizedPatch?.order))
      ? { order: Number(normalizedPatch.order) }
      : {}),

    lifecycle: {
      archived: normalizedPatch.status === MANAGED_FIELD_STATUS.ARCHIVED,
    },
    version: nextVersion,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };

  await updateDoc(ref, payload);

  console.log('[managedFields] SmartLists updated managed field successfully:', {
    organizationId,
    managedFieldId: normalizedManagedFieldId,
    version: nextVersion,
    type: normalizedPatch.type,
    optionsSetId: normalizedPatch.optionsSource.optionsSetId,
  });

  return {
    status: 'success',
    message: 'Managed field updated successfully.',
    managedFieldId: normalizedManagedFieldId,
    data: {
      id: normalizedManagedFieldId,
      ...payload,
    },
  };
};